/**
 * @fileoverview Distance maths for the Opportunities map.
 *
 * The server sends coordinates and an origin; the CLIENT computes distance.
 * That split is deliberate — moving the radius slider or searching another
 * area then needs no round trip, which is what makes the map feel like a map
 * rather than a form that reloads.
 *
 * Mirrors `backend/services/geocoding.haversine_miles`; the fixture table in
 * `__tests__/geo.test.js` is the same one the backend suite runs.
 */

const EARTH_RADIUS_KM = 6371.0088;
const MILES_PER_KM = 0.621371;

/** Radius choices, in miles. 25 is the default — see RADIUS_DEFAULT. */
export const RADIUS_OPTIONS = [10, 25, 50, 100, 250];
export const RADIUS_DEFAULT = 25;

const rad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in miles between two `{latitude, longitude}` points. */
export function distanceMiles(a, b) {
  if (!hasCoords(a) || !hasCoords(b)) return null;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h)) * MILES_PER_KM;
}

/** Whether a thing carries usable coordinates. `0` is a valid latitude. */
export function hasCoords(p) {
  return !!p && typeof p.latitude === "number" && typeof p.longitude === "number";
}

/**
 * Annotate listings with `distance_miles` from `origin` (null when either end
 * lacks coordinates), nearest first, and unlocated listings LAST rather than
 * dropped.
 */
export function withDistance(programs, origin) {
  const rows = (programs || []).map((p) => ({ ...p, distance_miles: distanceMiles(origin, p) }));
  rows.sort((a, b) => {
    if (a.distance_miles == null && b.distance_miles == null) return 0;
    if (a.distance_miles == null) return 1;
    if (b.distance_miles == null) return -1;
    return a.distance_miles - b.distance_miles;
  });
  return rows;
}

/**
 * Filter to those within `radius` miles.
 *
 * **A listing with no distance is always KEPT.** `location` only became a
 * geocoded field recently, so most existing programs have no coordinates —
 * dropping them would empty the tab and look like the feature is broken. The
 * UI labels them instead ("Location not set"), which is the honest answer.
 * Passing no origin or no radius filters nothing, for the same reason.
 */
export function withinRadius(rows, radius) {
  if (!radius) return rows;
  return (rows || []).filter((p) => p.distance_miles == null || p.distance_miles <= radius);
}

/** "12 miles away" / "Less than a mile away" / null when unknown. */
export function distanceLabel(miles) {
  if (miles == null) return null;
  if (miles < 1) return "Less than a mile away";
  return `${Math.round(miles)} mile${Math.round(miles) === 1 ? "" : "s"} away`;
}

/**
 * A map view that shows every point plus the origin.
 *
 * Returned as bounds rather than a center+zoom so Leaflet does the fitting —
 * a computed zoom is guesswork that clips markers at the edges. Null when
 * there is nothing to frame, and the caller falls back to a default view.
 */
export function boundsFor(points) {
  const usable = (points || []).filter(hasCoords);
  if (usable.length === 0) return null;
  const lats = usable.map((p) => p.latitude);
  const lngs = usable.map((p) => p.longitude);
  return [
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)],
  ];
}

/**
 * Which of a program's instruments this instructor already teaches.
 *
 * Case- and whitespace-insensitive, because both sides are free text: an org
 * types "Violin" into a program form and an instructor typed "violin" during
 * onboarding, and a match nobody can see is worse than no matching at all.
 *
 * Returns the program's OWN spelling of the matches, so the UI highlights the
 * words actually on screen rather than echoing the instructor's casing back.
 */
export function matchingInstruments(programInstruments, teaches) {
  const mine = new Set((teaches || []).map((i) => String(i).trim().toLowerCase()).filter(Boolean));
  if (mine.size === 0) return [];
  return (programInstruments || []).filter((i) => mine.has(String(i).trim().toLowerCase()));
}
